import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import UserLayout from '../../components/user/UserLayout';
import toast from 'react-hot-toast';
import {
  Server, XCircle, AlertTriangle, RefreshCw, TrendingUp, TrendingDown,
  Wallet, Activity, BarChart2, Zap, Clock, Hash, Layers,
  ArrowUpRight, ArrowDownRight, Minus, Eye, EyeOff, Trash2
} from 'lucide-react';

const fmt = (n, d = 2) => n == null ? '—' : Number(n).toLocaleString('en-IN', { minimumFractionDigits: d, maximumFractionDigits: d });
const fmtC = (n, cur = 'USD') => n == null ? '—' : `${cur === 'USD' ? '$' : cur + ' '}${fmt(n)}`;

const buildAccountInfoFromStatus = (status) => {
  if (!status?.mt5Connected) return null;
  const data = status.mt5Data || {};
  return {
    connected: true,
    live: false,
    accountNumber: status.mt5Login,
    name: data.name || status.mt5FullName,
    server: status.mt5Server,
    currency: data.currency || 'USD',
    leverage: data.leverage || 0,
    balance: data.balance || 0,
    equity: data.equity || 0,
    margin: data.margin || 0,
    freeMargin: data.freeMargin || 0,
    marginLevel: data.marginLevel || 0,
    profit: data.profit || 0,
    openPositions: data.openPositions || 0,
    lastSyncedAt: data.lastSyncedAt,
    message: data.lastSyncedAt
      ? `Last synced: ${new Date(data.lastSyncedAt).toLocaleString('en-IN')}`
      : 'MT5 is connected. Account data will sync now or by 1:00 AM tonight.',
  };
};

const ProfitBadge = ({ value }) => {
  if (value == null) return <span className="text-gray-400">—</span>;
  const pos = value >= 0;
  return (
    <span className={`inline-flex items-center gap-1 font-semibold ${pos ? 'text-primary-400' : 'text-red-400'}`}>
      {pos ? <ArrowUpRight className="w-3.5 h-3.5" /> : <ArrowDownRight className="w-3.5 h-3.5" />}
      {pos ? '+' : ''}{fmt(value)}
    </span>
  );
};

const StatCard = ({ icon: Icon, label, value, valueClass = 'text-white', iconClass = 'text-primary-400', bgClass = 'bg-primary-500/20' }) => (
  <div className="bg-dark-700 border border-white/5 rounded-2xl p-5">
    <div className={`w-10 h-10 ${bgClass} rounded-xl flex items-center justify-center mb-3`}>
      <Icon className={`w-5 h-5 ${iconClass}`} />
    </div>
    <p className={`text-xl font-bold ${valueClass}`}>{value ?? '—'}</p>
    <p className="text-gray-400 text-sm mt-0.5">{label}</p>
  </div>
);

// Change 12: Connect form with new fields
function MT5ConnectForm({ onSubmit, loading }) {
  const [form, setForm] = useState({
    mt5FullName: '', mt5Login: '', mt5Server: '',
    mt5Password: '', mt5MasterPassword: '', mt5BrokerName: ''
  });
  const [showPw, setShowPw] = useState(false);
  const [showMasterPw, setShowMasterPw] = useState(false);

  const handleSubmit = (e) => { e.preventDefault(); onSubmit(form); };

  const inputClass = "w-full bg-dark-600 border border-white/10 text-white rounded-xl px-4 py-3 focus:outline-none focus:border-primary-500";
  const labelClass = "block text-sm font-medium text-gray-300 mb-1.5";

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <label className={labelClass}>Full Name (as on MT5)</label>
          <input type="text" value={form.mt5FullName}
            onChange={e => setForm(p => ({ ...p, mt5FullName: e.target.value }))}
            className={inputClass} placeholder="Your full name" required />
        </div>
        <div>
          <label className={labelClass}>MT5 Login ID</label>
          <input type="text" value={form.mt5Login}
            onChange={e => setForm(p => ({ ...p, mt5Login: e.target.value }))}
            className={`${inputClass} font-mono tracking-widest`} placeholder="e.g. 12345678" required />
        </div>
        <div>
          <label className={labelClass}>MT5 Server</label>
          <input type="text" value={form.mt5Server}
            onChange={e => setForm(p => ({ ...p, mt5Server: e.target.value }))}
            className={inputClass} placeholder="e.g. ICMarkets-Demo02" required />
        </div>
        <div>
          <label className={labelClass}>Broker Name</label>
          <input type="text" value={form.mt5BrokerName}
            onChange={e => setForm(p => ({ ...p, mt5BrokerName: e.target.value }))}
            className={inputClass} placeholder="e.g. IC Markets" required />
        </div>
        <div>
          <label className={labelClass}>MT5 Password</label>
          <div className="relative">
            <input type={showPw ? 'text' : 'password'} value={form.mt5Password}
              onChange={e => setForm(p => ({ ...p, mt5Password: e.target.value }))}
              className={`${inputClass} pr-12`} placeholder="Your MT5 password" required />
            <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-200">
              {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
        </div>
        <div>
          <label className={labelClass}>Master Password <span className="text-gray-500 text-xs">(optional)</span></label>
          <div className="relative">
            <input type={showMasterPw ? 'text' : 'password'} value={form.mt5MasterPassword}
              onChange={e => setForm(p => ({ ...p, mt5MasterPassword: e.target.value }))}
              className={`${inputClass} pr-12`} placeholder="MT5 master password" />
            <button type="button" onClick={() => setShowMasterPw(!showMasterPw)} className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-200">
              {showMasterPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
        </div>
      </div>

      <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-3 flex items-start gap-2">
        <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
        <p className="text-amber-300 text-xs">Your credentials are stored securely and used only for automated profit calculation.</p>
      </div>
      <button type="submit" disabled={loading}
        className="w-full bg-primary-600 hover:bg-primary-500 disabled:bg-primary-800 text-white font-semibold py-3 rounded-xl transition-colors flex items-center justify-center gap-2">
        {loading ? <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
          : <><Server className="w-4 h-4" /> Connect MT5 Account</>}
      </button>
    </form>
  );
}

export default function MT5Page() {
  const [mt5Status, setMt5Status] = useState(null);
  const [accountInfo, setAccountInfo] = useState(null);
  const [openTrades, setOpenTrades] = useState([]);
  const [history, setHistory] = useState([]);
  const [tab, setTab] = useState('trades');
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [infoLoading, setInfoLoading] = useState(false);
  const [showReconnectForm, setShowReconnectForm] = useState(false);

  useEffect(() => {
    axios.get('/api/mt5/status').then(r => setMt5Status(r.data));
  }, []);

  useEffect(() => {
    if (!mt5Status?.mt5Connected) return;
    if (mt5Status.mt5Data) {
      setAccountInfo(buildAccountInfoFromStatus(mt5Status));
    }
    fetchLiveData();
  }, [mt5Status?.mt5Connected]);

  const fetchLiveData = useCallback(async (showRefresh = false) => {
    if (showRefresh) setRefreshing(true); else setInfoLoading(true);
    try {
      const [infoRes, tradesRes, histRes] = await Promise.all([
        axios.get('/api/mt5/account-info'),
        axios.get('/api/mt5/open-trades'),
        axios.get('/api/mt5/history')
      ]);
      if (infoRes.data?.connected) setAccountInfo(infoRes.data);
      setOpenTrades(tradesRes.data.trades || []);
      setHistory(histRes.data.history || []);
    } catch (err) {
      if (showRefresh) toast.error('Failed to refresh data');
    } finally {
      setRefreshing(false); setInfoLoading(false);
    }
  }, []);

  const handleConnect = async (form) => {
    setLoading(true);
    try {
      const connectRes = await axios.post('/api/mt5/connect', form);
      // Show success or warning from backend/VPS
      if (connectRes.data?.warning) {
        toast.error(`Warning: ${connectRes.data.message}`);
      } else {
        toast.success('MT5 account connected successfully!');
      }

      if (connectRes.data?.mt5Data) {
        setAccountInfo({
          connected: true,
          live: false,
          accountNumber: connectRes.data.mt5Login || form.mt5Login,
          name: connectRes.data.mt5Data.name || form.mt5FullName,
          server: connectRes.data.mt5Server || form.mt5Server,
          currency: connectRes.data.mt5Data.currency || 'USD',
          leverage: connectRes.data.mt5Data.leverage || 0,
          balance: connectRes.data.mt5Data.balance || 0,
          equity: connectRes.data.mt5Data.equity || 0,
          margin: connectRes.data.mt5Data.margin || 0,
          freeMargin: connectRes.data.mt5Data.freeMargin || 0,
          marginLevel: connectRes.data.mt5Data.marginLevel || 0,
          profit: connectRes.data.mt5Data.profit || 0,
          openPositions: connectRes.data.mt5Data.openPositions || 0,
          lastSyncedAt: connectRes.data.mt5Data.lastSyncedAt,
          message: connectRes.data.message || 'MT5 account connected and data fetched.',
        });
      }

      const statusRes = await axios.get('/api/mt5/status');
      setMt5Status(statusRes.data);
    } catch (err) {
      const serverMsg = err.response?.data?.message || err.response?.data?.error || err.message || 'Connection failed';
      const errorCode = err.response?.data?.errorCode || err.response?.data?.code || null;

      // Heuristic: common MT5 terminal busy/login messages
      const busyPattern = /already connected|trade context busy|terminal busy|already logged/i;
      if (busyPattern.test(String(serverMsg)) || (errorCode && [3,4,10004,10009].includes(Number(errorCode)))) {
        toast.error(`${serverMsg} — If this persists, restart the VPS MT5 bridge or the MT5 terminals, or use Disconnect in admin.`);
      } else {
        toast.error(serverMsg);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm('Are you sure you want to disconnect your MT5 account? You can connect a new account after this.')) return;
    try {
      await axios.delete('/api/mt5/disconnect');
      toast.success('MT5 account disconnected. You can now connect a new account.');
      setMt5Status({ mt5Connected: false });
      setAccountInfo(null); setOpenTrades([]); setHistory([]);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Disconnect failed');
    }
  };

  const cur = accountInfo?.currency || 'USD';
  const isConnectedButNotLive = accountInfo?.connected && accountInfo?.live === false;
  const mt5SyncMessage = accountInfo?.message || 'Live MT5 data is not available yet. It will sync automatically at 1:00 AM.';

  if (!mt5Status?.mt5Connected) {
    return (
      <UserLayout>
        <div className="max-w-2xl animate-fade-in">
          <h1 className="text-2xl font-bold text-white mb-2">MT5 Account</h1>
          <p className="text-gray-400 mb-6">Connect your MetaTrader 5 account to enable automated profit tracking</p>

          <div className="bg-red-500/10 border border-red-500/30 rounded-2xl p-4 mb-6 flex items-center gap-3">
            <XCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
            <p className="text-red-300 text-sm">No MT5 account connected.</p>
          </div>

          <div className="bg-dark-700 border border-white/5 rounded-2xl p-6 mb-4">
            <h3 className="text-white font-semibold mb-4">Connect MT5 Account</h3>
            <MT5ConnectForm onSubmit={handleConnect} loading={loading} />
          </div>

          {/* <div className="bg-dark-700 border border-white/5 rounded-2xl p-5">
            <p className="text-white font-semibold text-sm mb-3">Python MT5 Setup</p>
            <div className="space-y-1.5 text-sm text-gray-400">
              <p>1. Install MT5 terminal on your Windows machine</p>
              <p>2. Install Python package: <code className="bg-dark-600 px-1 rounded text-primary-300">pip install MetaTrader5</code></p>
              <p>3. Ensure Python is available as <code className="bg-dark-600 px-1 rounded text-primary-300">python3</code> on server PATH</p>
              <p>4. Script is at: <code className="bg-dark-600 px-1 rounded text-primary-300">backend/utils/mt5_bridge.py</code></p>
            </div>
          </div> */}
        </div>
      </UserLayout>
    );
  }

  return (
    <UserLayout>
      <div className="animate-fade-in">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold text-white">MT5 Dashboard</h1>
            <p className="text-gray-400 text-sm mt-1">
              {mt5Status?.mt5BrokerName && <span className="text-primary-400">{mt5Status.mt5BrokerName} • </span>}
              Live account data via MetaTrader5
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button onClick={() => fetchLiveData(true)} disabled={refreshing}
              className="flex items-center gap-2 bg-dark-700 border border-white/5 text-gray-300 hover:text-white px-3 py-2 rounded-xl text-sm transition-colors">
              <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin text-primary-400' : ''}`} />
              {refreshing ? 'Refreshing...' : 'Refresh'}
            </button>
            {/* <button onClick={() => setShowReconnectForm(v => !v)}
              className="flex items-center gap-2 bg-dark-700 border border-white/10 text-gray-300 hover:text-white px-3 py-2 rounded-xl text-sm transition-colors">
              <Server className="w-4 h-4" /> {showReconnectForm ? 'Cancel' : 'Change Account'}
            </button> */}
            <button onClick={handleDisconnect}
              className="flex items-center gap-2 bg-red-600/20 border border-red-500/30 text-red-400 hover:bg-red-600/30 px-3 py-2 rounded-xl text-sm font-medium transition-colors">
              <Trash2 className="w-4 h-4" /> Disconnect
            </button>
          </div>
        </div>

        <div className={`flex items-center gap-3 rounded-2xl p-4 mb-6 border ${accountInfo?.live ? 'bg-primary-500/10 border-primary-500/30' : 'bg-amber-500/10 border-amber-500/30'}`}>
          <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${accountInfo?.live ? 'bg-primary-500 animate-pulse' : 'bg-amber-500'}`}></div>
          <div className="flex-1 min-w-0">
            {infoLoading ? <p className="text-gray-400 text-sm">Connecting to MT5 terminal...</p>
              : accountInfo?.live
                ? <p className="text-primary-300 text-sm font-medium">Live data from MetaTrader5 Python</p>
                : <p className="text-amber-300 text-sm">{accountInfo?.message || (mt5Status?.mt5Connected ? 'MT5 account is connected. Account data will sync now or by 1:00 AM tonight.' : (!mt5Status?.vpsConfigured ? 'Configure MT5_VPS_URL in your .env file to enable live data.' : 'MT5 terminal is not reachable on the VPS.'))}</p>}
          </div>
          <div className="text-right text-xs text-gray-500 flex-shrink-0">
            <p>Login: <span className="text-white font-mono">{accountInfo?.accountNumber || mt5Status?.mt5Login}</span></p>
            <p>Server: <span className="text-white">{accountInfo?.server || mt5Status?.mt5Server}</span></p>
          </div>
        </div>

        {showReconnectForm && (
          <div className="bg-dark-700 border border-white/5 rounded-2xl p-6 mb-4">
            <h3 className="text-white font-semibold mb-4">Update MT5 Account</h3>
            <MT5ConnectForm onSubmit={async (form) => { await handleConnect(form); setShowReconnectForm(false); }} loading={loading} />
          </div>
        )}

        {infoLoading && (
          <div className="flex items-center justify-center h-48">
            <div className="text-center">
              <div className="w-10 h-10 border-4 border-primary-500 border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
              <p className="text-gray-400 text-sm">Fetching live MT5 data...</p>
            </div>
          </div>
        )}

        {!infoLoading && accountInfo && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
              {[
                { icon: Hash, label: 'Account No.', value: accountInfo.accountNumber, mono: true, ic: 'text-blue-400', bg: 'bg-blue-500/20' },
                { icon: Layers, label: 'Leverage', value: `1:${accountInfo.leverage || '—'}`, ic: 'text-purple-400', bg: 'bg-purple-500/20' },
                { icon: Server, label: 'Server', value: accountInfo.server, ic: 'text-amber-400', bg: 'bg-amber-500/20' },
                { icon: Activity, label: 'Open Trades', value: openTrades.length, ic: 'text-primary-400', bg: 'bg-primary-500/20' },
              ].map(({ icon: Icon, label, value, mono, ic, bg }) => (
                <div key={label} className="bg-dark-700 border border-white/5 rounded-xl p-4 flex items-center gap-3">
                  <div className={`w-9 h-9 ${bg} rounded-xl flex items-center justify-center flex-shrink-0`}>
                    <Icon className={`w-4 h-4 ${ic}`} />
                  </div>
                  <div className="min-w-0">
                    <p className="text-gray-500 text-xs">{label}</p>
                    <p className={`text-white font-semibold text-sm truncate ${mono ? 'font-mono' : ''}`}>{value}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 mb-4">
              <StatCard icon={Wallet} label="Balance" value={fmtC(accountInfo.balance, cur)} />
              <StatCard icon={TrendingUp} label="Equity" value={fmtC(accountInfo.equity, cur)}
                valueClass={accountInfo.equity >= (accountInfo.balance||0) ? 'text-primary-400' : 'text-red-400'}
                iconClass="text-blue-400" bgClass="bg-blue-500/20" />
              <StatCard icon={BarChart2} label="Margin Used" value={fmtC(accountInfo.margin, cur)}
                iconClass="text-amber-400" bgClass="bg-amber-500/20" />
              <StatCard icon={Zap} label="Free Margin" value={fmtC(accountInfo.freeMargin, cur)}
                valueClass={(accountInfo.freeMargin||0) > 0 ? 'text-primary-400' : 'text-red-400'}
                iconClass="text-green-400" bgClass="bg-green-500/20" />
              <div className="bg-dark-700 border border-white/5 rounded-2xl p-5">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-3 ${(accountInfo.profit||0)>=0 ? 'bg-primary-500/20' : 'bg-red-500/20'}`}>
                  {(accountInfo.profit||0) >= 0 ? <TrendingUp className="w-5 h-5 text-primary-400" /> : <TrendingDown className="w-5 h-5 text-red-400" />}
                </div>
                <p className={`text-xl font-bold ${(accountInfo.profit||0) >= 0 ? 'text-primary-400' : 'text-red-400'}`}>
                  {(accountInfo.profit||0) >= 0 ? '+' : ''}{fmtC(accountInfo.profit, cur)}
                </p>
                <p className="text-gray-400 text-sm mt-0.5">Floating P&L</p>
              </div>
            </div>

            <div className="bg-dark-700 border border-white/5 rounded-2xl overflow-hidden">
              <div className="flex border-b border-white/5">
                <button onClick={() => setTab('trades')}
                  className={`flex-1 py-3.5 text-sm font-medium flex items-center justify-center gap-2 transition-colors ${tab==='trades' ? 'text-primary-400 border-b-2 border-primary-500 bg-primary-500/5' : 'text-gray-400 hover:text-white'}`}>
                  <Activity className="w-4 h-4" /> Open Trades ({openTrades.length})
                </button>
                <button onClick={() => setTab('history')}
                  className={`flex-1 py-3.5 text-sm font-medium flex items-center justify-center gap-2 transition-colors ${tab==='history' ? 'text-primary-400 border-b-2 border-primary-500 bg-primary-500/5' : 'text-gray-400 hover:text-white'}`}>
                  <Clock className="w-4 h-4" /> Trade History ({history.length})
                </button>
              </div>

              {tab === 'trades' && (
                isConnectedButNotLive ? (
                  <div className="py-12 text-center">
                    <Server className="w-10 h-10 text-gray-600 mx-auto mb-3" />
                    <p className="text-gray-400">{mt5SyncMessage}</p>
                  </div>
                ) : !accountInfo?.connected ? (
                  <div className="py-12 text-center"><Server className="w-10 h-10 text-gray-600 mx-auto mb-3" /><p className="text-gray-400">MT5 is not connected.</p></div>
                ) : openTrades.length === 0 ? (
                  <div className="py-12 text-center"><Minus className="w-10 h-10 text-gray-600 mx-auto mb-3" /><p className="text-gray-400">No open positions.</p></div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead><tr className="border-b border-white/5">
                        {['Symbol','Type','Lots','Open Price','Current','P&L','Swap','SL','TP','Opened At'].map(h => (
                          <th key={h} className={`px-4 py-3 text-gray-400 font-medium ${['Symbol','Type','Opened At'].includes(h) ? 'text-left' : 'text-right'}`}>{h}</th>
                        ))}
                      </tr></thead>
                      <tbody>
                        {openTrades.map((t, i) => (
                          <tr key={t.id||i} className="border-b border-white/5 hover:bg-white/2">
                            <td className="px-4 py-3 text-white font-bold">{t.symbol}</td>
                            <td className="px-4 py-3">
                              <span className={`px-2 py-0.5 rounded text-xs font-semibold ${t.type==='BUY' ? 'bg-primary-500/20 text-primary-300' : 'bg-red-500/20 text-red-300'}`}>{t.type}</span>
                            </td>
                            <td className="px-4 py-3 text-right text-white">{t.volume}</td>
                            <td className="px-4 py-3 text-right text-gray-300 font-mono">{fmt(t.openPrice, 5)}</td>
                            <td className="px-4 py-3 text-right text-gray-300 font-mono">{fmt(t.currentPrice, 5)}</td>
                            <td className="px-4 py-3 text-right"><ProfitBadge value={t.profit} /></td>
                            <td className="px-4 py-3 text-right text-gray-400">{fmt(t.swap)}</td>
                            <td className="px-4 py-3 text-right text-gray-400">{t.sl ? fmt(t.sl,5) : '—'}</td>
                            <td className="px-4 py-3 text-right text-gray-400">{t.tp ? fmt(t.tp,5) : '—'}</td>
                            <td className="px-4 py-3 text-gray-400 text-xs">{t.time ? new Date(t.time).toLocaleString('en-IN',{day:'2-digit',month:'short',hour:'2-digit',minute:'2-digit'}) : '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )
              )}

              {tab === 'history' && (
                isConnectedButNotLive ? (
                  <div className="py-12 text-center">
                    <Clock className="w-10 h-10 text-gray-600 mx-auto mb-3" />
                    <p className="text-gray-400">{mt5SyncMessage}</p>
                  </div>
                ) : !accountInfo?.connected ? (
                  <div className="py-12 text-center"><Clock className="w-10 h-10 text-gray-600 mx-auto mb-3" /><p className="text-gray-400">MT5 is not connected.</p></div>
                ) : history.length === 0 ? (
                  <div className="py-12 text-center"><Clock className="w-10 h-10 text-gray-600 mx-auto mb-3" /><p className="text-gray-400">No trade history found for the last 30 days.</p></div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead><tr className="border-b border-white/5">
                        {['Symbol','Type','Lots','Price','Profit','Commission','Swap','Closed At'].map(h => (
                          <th key={h} className={`px-4 py-3 text-gray-400 font-medium ${['Symbol','Type','Closed At'].includes(h) ? 'text-left' : 'text-right'}`}>{h}</th>
                        ))}
                      </tr></thead>
                      <tbody>
                        {history.map((d, i) => (
                          <tr key={d.id||i} className="border-b border-white/5 hover:bg-white/2">
                            <td className="px-4 py-3 text-white font-bold">{d.symbol || '—'}</td>
                            <td className="px-4 py-3">
                              {d.symbol
                                ? <span className={`px-2 py-0.5 rounded text-xs font-semibold ${d.type==='BUY' ? 'bg-primary-500/20 text-primary-300' : 'bg-red-500/20 text-red-300'}`}>{d.type}</span>
                                : <span className="text-xs text-gray-500">BALANCE</span>}
                            </td>
                            <td className="px-4 py-3 text-right text-gray-300">{d.volume ?? '—'}</td>
                            <td className="px-4 py-3 text-right text-gray-300 font-mono">{fmt(d.openPrice, 5)}</td>
                            <td className="px-4 py-3 text-right"><ProfitBadge value={d.profit} /></td>
                            <td className="px-4 py-3 text-right text-gray-400">{fmt(d.commission)}</td>
                            <td className="px-4 py-3 text-right text-gray-400">{fmt(d.swap)}</td>
                            <td className="px-4 py-3 text-gray-400 text-xs">{d.time ? new Date(d.time).toLocaleString('en-IN',{day:'2-digit',month:'short',hour:'2-digit',minute:'2-digit'}) : '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )
              )}
            </div>
          </>
        )}
      </div>
    </UserLayout>
  );
}
